import {
    IMG_CLASS,
    IMG_PROXY_API,
    isOffline,
    REGEXP_ALL_IMG,
    REGEXP_ALL_IMG_TAGS,
    REGEXP_IMG_DEL_BR,
    REGEXP_IMG_SRC,
    REGEXP_IMG_SRC_URL,
    TEST_HOST,
    THUMB_PROCESS_PARAMS,
} from '../constants';
import {countLog} from '../log';
import {
    isAndroid,
    OSVersion,
    UA,
    versionCompare,
} from './tools';

interface StyleData {
    singleImg?: string;
    doubleImgContainer?: string;
    threeImgContainer?: string;
    fourImgContainer?: string;
    emojiImg?: string;
    wAutoRatio3X4?: string;
    hAutoRatio3X4?: string;
    wAutoRatio4X3?: string;
    hAutoRatio4X3?: string;
    ratioItem?: string;
}
interface dealImageTextPayload {
    content?: string;
    $style: StyleData;
    showAvatar?: string;
    isEmoji?: boolean;
    imageSize?: string;
}

function processXbceUrl(imgUrl: string, processParamStr?: string) {
    if (!imgUrl) {
        return '';
    }

    const url = new URL(imgUrl);
    // todo dkq：后续下了，可以删除
    if (Math.random() < 0.01) {
        url.searchParams.set('_fe_sid', '_minify_img');
        return url.toString();
    }

    const processParam = processParamStr || '/format,f_auto/quality,q_75';
    // 检查路径是否为XXX.cdn.bcebos.com/xxx，该规则只在bos下生效
    // 文档参考地址：https://cloud.baidu.com/doc/BOS/s/7ldh5wpk6
    if (url.hostname.endsWith('cdn.bcebos.com')) {
        // 检查是否缺少x-bce-process参数
        const xBceProcess = url.searchParams.get('x-bce-process');
        if (!xBceProcess) {
            url.searchParams.set('x-bce-process', `image${processParam}`);
        }
        else if (xBceProcess.startsWith('image/')) {
            // 仅处理使用x-bce-process=image/xxx 格式的图片
            url.searchParams.set('x-bce-process', `${xBceProcess}${processParam}`);
        }
    }
    return url.toString();
}

function processImg(markdown?: string, processParamStr?: string) {
    // markdown是空字符串
    if (!markdown) {
        return markdown;
    }
    // 使用正则表达式提取img标签中的src属性
    const imgTagRegex = /<img\s+src="([^"]+)"\s*\/?>/g;
    let updatedMarkdown = markdown;

    // 循环处理所有匹配的img标签
    updatedMarkdown = updatedMarkdown.replace(imgTagRegex, (match, src) => {
        try {
            const url = new URL(src);
            if (url.hostname.endsWith('cdn.bcebos.com')) {
                const compressedSrc = processXbceUrl(src, processParamStr);
                countLog('processImg', {
                    processImg: 1,
                });
                return `<img src="${compressedSrc}" data-raw-src="${src}" decoding="sync" loading="lazy" />`;
            }
        }
        catch (e) {
            return match;
        }
        // 如果不需要处理，则返回带decoding="sync"属性的原始img标签
        // eslint-disable-next-line xss/no-mixed-html
        return match.replace(/<img/, '<img decoding="sync" loading="lazy"');
    });

    return updatedMarkdown;
}

function getImageInfo(url: string) {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve({
            width: img.width, height: img.height,
        });
        img.onerror = () => resolve({
            width: 0, height: 0,
        });
        img.src = url;
    });
}

// 获取首张图片缩略图地址计算并返回图片比例
async function getImgSize(content: string, $style: StyleData) {
    const match = content.match(REGEXP_IMG_SRC_URL);
    const firstImgUrl = match ? match[1] : '';
    if (!firstImgUrl) {
        return '';
    }
    const thumbURL = processXbceUrl(firstImgUrl, THUMB_PROCESS_PARAMS);
    const {width, height} = (await getImageInfo(thumbURL) || {}) as {width: number; height: number};
    if (!width || !height) {
        return '';
    }
    // 图片宽高比容差
    const ratioDiff = 0.05;
    // 计算宽高比
    const ratio = width / height;
    // 根据宽高比设置图片尺寸
    if (Math.abs(ratio - 1) < ratioDiff) {
        return '';
    }
    else if (ratio > 1) {
        return ratio > 4 / 3 ? $style.wAutoRatio4X3 : $style.hAutoRatio4X3;
    }
    return ratio > 3 / 4 ? $style.wAutoRatio3X4 : $style.hAutoRatio3X4;
}

function dealImageTextContent(payload: dealImageTextPayload) {
    const {$style, isEmoji = false, imageSize = ''} = payload;
    let content = payload.content || '';
    // 使用正则表达式匹配所有img标签,根据img标签设置不同样式
    const imgValues = content.match(REGEXP_IMG_SRC) || [];
    const imgLen = imgValues.length;
    const emojiClass = (isEmoji ? $style.emojiImg : $style.singleImg) || '';
    let imgClass = '';
    switch (imgLen) {
        case 1:
            // 单张图时，假如是表情包，添加表情包图片样式
            imgClass = emojiClass;
            break;
        case 2:
            imgClass = $style.doubleImgContainer || '';
            break;
        case 3:
            imgClass = $style.threeImgContainer || '';
            break;
        case 4:
            imgClass = `${$style.doubleImgContainer} ${$style.fourImgContainer}`;
            break;
        default:
            return content;
    }
    try {
        if (content.includes(imgClass)) {
            return content;
        }
        const imageData = !imageSize ? `data-content="${encodeURIComponent(content)}"` : '';
        const imageItemSize = !isEmoji ? `${imageSize} ${$style.ratioItem}` : '';
        // eslint-disable-next-line
        // 1. 去掉 img 标签后的 <br>
        content = content.replace(REGEXP_IMG_DEL_BR, '$1');
        // 2. 使用正则表达式将每个 <img> 标签用 <div> 包裹，并给 <img> 添加class="draw-image-item"
        content = content.replace(REGEXP_ALL_IMG_TAGS,
        // eslint-disable-next-line max-len
            `<div --x-custom-- class="draw-image-item ${imageItemSize}" ${imageData}><img class="${IMG_CLASS.LONG_PRESS_ABLE_IMG}"$2</div>`);
        // 3、最后将所有包含 <img> 的 <div> 部分整体包裹在 class 为 "imgClass" 的 <div> 中,利用display:grid 实现多张图片的布局
        // eslint-disable-next-line max-len
        content = content.replace(/((<div --x-custom-- class="draw-image-item.*?<\/div>)+)/, `<div class="${imgClass}">$1</div>`);

        return content;
    }
    catch (error) {
        return content;
    }
};

function encodeImgUrl(url: string): string {
    const isSupportWatermask = isAndroid
        || versionCompare(OSVersion, '14.0') >= 0
        || !UA.includes('Mac');

    if (isOffline) {
        url.replace('http://eb118-file.cdn.bcebos.com/', TEST_HOST);
    }

    return isSupportWatermask
        ? url
        : url.replace(/<img\s+src="(.+?)"\s*\/?>/g, (m, n) => {
            const [
                uri,
                query = '',
            ] = n.split('?');
            return m.replace(n, [
                uri,
                encodeURIComponent(query),
            ].join('?'));
        });
};

/**
 * 来自搜索图片处理逻辑，走的是bos v1的规则
 * @param url 图片原始链接
 * @param quality 压缩质量
 * @param width 压缩的宽度
 * @param height 压缩的高度
 * @returns 处理后的图片链接
 */
function processSearchImgUrl(url: string, quality?: number, width?: number, height?: number): string {
    const urlObject = new URL(url);
    let {pathname} = urlObject;

    // 添加 size 参数
    let sizeParam = '';
    if (width !== undefined && height !== undefined) {
        sizeParam = `b${width},${height}`;
    }
    else if (width !== undefined) {
        sizeParam = `w${width}`;
    }
    else if (height !== undefined) {
        sizeParam = `h${height}`;
    }
    const sizeRegex = /size=[^&]*/;
    if (sizeRegex.test(pathname)) {
        pathname = pathname.replace(sizeRegex, `size=${sizeParam}`);
    }
    else if (sizeParam) {
        pathname = pathname.endsWith('&')
            ? pathname + `size=${sizeParam}`
            : pathname + `&size=${sizeParam}`;
    }

    // 添加 quality 参数
    if (quality !== undefined) {
        const qualityRegex = /q=[^&]*/;
        if (qualityRegex.test(pathname)) {
            pathname = pathname.replace(qualityRegex, `q=${quality}`);
        }
        else {
            pathname = pathname.endsWith('&') ? pathname + `q=${quality}` : pathname + `&q=${quality}`;
        }
    }

    pathname += '&fmt=auto';

    // 更新路径
    urlObject.pathname = pathname;

    return urlObject.toString();
}

/**
 * 来自feed的图片处理逻辑，走的是bos v1的规则
 * @param url 图片原始链接
 * @param quality 压缩质量
 * @param width 压缩的宽度
 * @param height 压缩的高度
 * @returns 处理后的图片链接
 */
function processFeedImgUrl(url: string, quality?: number, width?: number, height?: number): string {
    // 分割URL，找到 '?'，将 base URL 和 query string 分开
    const [
        baseUrl,
        queryString,
    ] = url.split('?');

    // 查找是否有 '@' 参数
    const atIndex = baseUrl.indexOf('@');
    let newBaseUrl = baseUrl;

    // 构建新的参数部分，只有当相应的值存在时才添加
    // 默认自适应图片格式，可以是webp
    let newParams = '@f_auto,';
    let separator = '';

    if (width || height) {
        newParams += 's_0';
        separator = ',';
    }
    if (width !== undefined) {
        newParams += `,w_${width}`;
    }
    if (height !== undefined) {
        newParams += `,h_${height}`;
    }
    if (quality !== undefined) {
        newParams += `${separator}q_${quality}`;
    }

    if (atIndex !== -1) {
        // 如果有 '@'，替换掉 '@' 后面的所有参数为新的参数
        newBaseUrl = baseUrl.slice(0, atIndex) + newParams;
    }
    else {
        // 如果没有 '@'，在文件名后添加新的参数部分
        newBaseUrl = `${baseUrl}${newParams}`;
    }

    // 重新组合 URL，并返回
    return queryString ? `${newBaseUrl}?${queryString}` : newBaseUrl;
}

function processHcgImgUrl(url: string, mWidth?: number, mHeight?: number, quality = 75): string {
    // ?x-bce-process=image/resize,m_pad,w_${mWidth},h_${mHeight},radius_50,sigma_50
    // image%2Fresize%2Cm_pad%2Cw_800%2Ch_450%2Cradius_50%2Csigma_50/quality,q_50/format,f_auto
    const queryParams = `/resize,m_pad,w_${mWidth},h_${mHeight},radius_50,sigma_50/quality,q_${quality}/format,f_auto`;
    return processXbceUrl(url, queryParams);
}

const feedRegex = /pics[0-9]*?\.baidu\.com/;
function fromFeed(url: string): boolean {
    return feedRegex.test(url);
}

const searchRegexList = [
    /t[0-9]*?\.baidu\.com/,
    /gips[0-9]*?\.baidu\.com/,
];

function fromSearch(url: string): boolean {
    return searchRegexList.some(regex => regex.test(url));
}

function processRecommendImgUrl(url: string, quality?: number, width?: number, height?: number): string {
    if (!url) {
        return url;
    }
    // todo dkq：后续下了，可以删除
    try {
        const imgUrl = new URL(url);
        if (Math.random() < 0.01) {
            imgUrl.searchParams.set('_fe_sid', '_minify_img');
            return imgUrl.toString();
        }
        if (fromFeed(imgUrl.toString())) {
            return processFeedImgUrl(url, quality, width, height);
        }
        if (fromSearch(imgUrl.toString())) {
            return processSearchImgUrl(url, quality, width, height);
        }
        return url;
    }
    catch (e) {
        console.error('invalid url: ', e);
        return url;
    }
}

function blob2base64(blob: Blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
function img2base64(url: string) {
    return fetch(`${IMG_PROXY_API}?url=${url}&responseType=blob`)
        .then(res => res.blob())
        .then(blob => blob2base64(blob))
        .catch(console.error);
}

/**
 * 生图成功添加图片比例class
 */

async function addImageRatioClass(content: string, $dom: HTMLElement, $style: StyleData) {
    try {
        if (!content || !REGEXP_ALL_IMG.test(content)) {
            return;
        }
        const imgList = $dom?.querySelectorAll('.draw-image-item');
        const imgSize = await getImgSize(content, $style);
        if (!imgSize) {
            return;
        }
        imgList?.forEach((img: any) => {
            img.classList.add(imgSize);
        });
    }
    catch (e) {
        console.error(e);
    }
}

/**
 * 根据图片比例重绘历史图片
 */
async function drawImg($container: HTMLElement, $style: any) {
    const $imgs = $container.querySelectorAll('.draw-image-item');
    if ($imgs?.length === 0) {
        return false;
    }
    for (const $img of Array.from($imgs)) {
        try {
            const {content = ''} = ($img as HTMLElement).dataset || {};
            if (!content) {
                return;
            }
            const data = decodeURIComponent(content);
            addImageRatioClass(data, $container, $style);
            $img.removeAttribute('data-content');
        }
        catch (e) {
            console.error(e);
        }
    }
}

export {
    addImageRatioClass,
    blob2base64,
    dealImageTextContent,
    drawImg,
    encodeImgUrl,
    getImgSize,
    img2base64,
    processFeedImgUrl,
    processHcgImgUrl,
    processImg,
    processRecommendImgUrl,
    processSearchImgUrl,
    processXbceUrl,
};
